import { Union } from "fable-compiler/bin/fable-library/Types";
import { logDebug } from "../js/Common";

const isDebugging = false;
function log(msg, ...params) { logDebug("CenterlinePlaneShared", isDebugging, msg, ...params); }

// Corresponds to the F# RAWMap.Models.Visualization.UnifiedDistanceBranch DU.
export const CenterlineDistanceBranchSelection = Object.freeze({
    LEFT: 'Left',
    RIGHT: 'Right',
    CUSP: 'Cusp'
});

export const CenterlineTransitionBranchSelection = Object.freeze({
    LEFT: 'Left',
    RIGHT: 'Right'
});

export const CenterlineBranchType = Object.freeze({
    DISTANCE: 'UnifiedDistanceBranch',
    TRANSITION: 'UnifiedTransitionBranch'
});

/** @typedef {{ centerlineBranchSelection: string, centerlineBranchType: string }} BranchSelection */

export const CenterlineBranchSelection = {
    /**
     * Converts F# BranchSelection DU instance to BranchSelection.
     * @param branchSelection {Union}
     * @return {BranchSelection}
     */
    ofBranchSelection: (branchSelection) => {
        return {
            centerlineBranchType: branchSelection.name,
            centerlineBranchSelection: branchSelection.fields[0].name
        };
    },
    /**
     * Determines if two instances of BranchSelection are equal.
     * @param a {BranchSelection}
     * @param b {BranchSelection}
     * @return {boolean}
     */
    areEqual: (a, b) => {
        return a.centerlineBranchType === b.centerlineBranchType &&
            a.centerlineBranchSelection === b.centerlineBranchSelection;
    },
    /**
     * Determines if the specified BranchSelection is valid.
     * @param branchSelection {BranchSelection}
     * @return {boolean}
     */
    isValid: (branchSelection) => {
        return [
            CenterlineBranchSelection.DISTANCE_LEFT,
            CenterlineBranchSelection.DISTANCE_RIGHT,
            CenterlineBranchSelection.DISTANCE_CUSP,
            CenterlineBranchSelection.TRANSITION_LEFT,
            CenterlineBranchSelection.TRANSITION_RIGHT
        ].some((value) => {
            return CenterlineBranchSelection.areEqual(value, branchSelection);
        });
    },
    DISTANCE_LEFT: {
        centerlineBranchType: CenterlineBranchType.DISTANCE,
        centerlineBranchSelection: CenterlineDistanceBranchSelection.LEFT
    },
    DISTANCE_RIGHT: {
        centerlineBranchType: CenterlineBranchType.DISTANCE,
        centerlineBranchSelection: CenterlineDistanceBranchSelection.RIGHT
    },
    DISTANCE_CUSP: {
        centerlineBranchType: CenterlineBranchType.DISTANCE,
        centerlineBranchSelection: CenterlineDistanceBranchSelection.CUSP
    },
    TRANSITION_LEFT: {
        centerlineBranchType: CenterlineBranchType.TRANSITION,
        centerlineBranchSelection: CenterlineTransitionBranchSelection.LEFT
    },
    TRANSITION_RIGHT: {
        centerlineBranchType: CenterlineBranchType.TRANSITION,
        centerlineBranchSelection: CenterlineTransitionBranchSelection.RIGHT
    }
};

export const CenterlineType = {
    LUMEN: 'Lumen',
    WALL: 'Wall',
};

export const AdditionalArrayNames = {
    LAST_INDEX_OF_TRANSITION_BRANCH_TRUNK: 'LastIndexOfTransitionBranchTrunk'
};

/**
 * Map branch-local index to master centerline index.
 * Trunk unchanged; left/right branches remapped to their master offsets.
 *
 * @param {number} branchIndex  - 0-based index within the unified branch polydata
 * @param {BranchSelection} branchSelection  - JS-object branch selection (not the raw F# Union)
 * @param centerlineFieldData  - VTK field data from the master centerline polydata
 * @returns {number} master centerline index
 */
export function convertUnifiedBranchIndexToMasterIndex(branchIndex, branchSelection, centerlineFieldData) {
    const [distanceTrunkMin, distanceTrunkMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk").getRange();
    const [distanceLeftMin] = centerlineFieldData.getArrayByName("Distance_Branch_Left").getRange();
    const [distanceRightMin] = centerlineFieldData.getArrayByName("Distance_Branch_Right").getRange();

    if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.DISTANCE_CUSP)) {
        return branchIndex;
    }

    if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.DISTANCE_LEFT)
        || CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.TRANSITION_LEFT)) {
        if (branchIndex <= distanceTrunkMax) {
            return branchIndex;
        } else {
            return (branchIndex - distanceTrunkMax - 1) + distanceLeftMin;
        }
    }

    if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.DISTANCE_RIGHT)
        || CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.TRANSITION_RIGHT)) {
        if (branchIndex <= distanceTrunkMax) {
            return branchIndex;
        } else {
            return (branchIndex - distanceTrunkMax - 1) + distanceRightMin;
        }
    }

    console.error("Unable to convert branch index to master index: ", branchIndex, branchSelection);
    return branchIndex;
}

/**
 * Map a master centerline index to its distance-branch selection and
 * branch-local index.  Inverse of {@link convertDistanceUnifiedBranchIndexToMasterIndex}.
 *
 * @param {number} masterIndex
 * @param centerlineFieldData - VTK field data from the master centerline polydata.
 * @returns {{ branch: string, index: number } | null} `null` when the required
 *          branch-range arrays are missing or `masterIndex` falls outside every
 *          known branch range.  Callers that treat an out-of-range index as an
 *          error should branch on `null` (or use the throwing
 *          `convertMasterToDistanceBranchIndex` wrapper in StentPlanning.js).
 */
export function tryConvertMasterToDistanceBranchIndex(masterIndex, centerlineFieldData) {
    const trunkCusp = centerlineFieldData.getArrayByName('Distance_Branch_Trunk_Cusp');
    const trunk = centerlineFieldData.getArrayByName('Distance_Branch_Trunk');
    const left = centerlineFieldData.getArrayByName('Distance_Branch_Left');
    const right = centerlineFieldData.getArrayByName('Distance_Branch_Right');
    if (!trunkCusp || !trunk || !left || !right) return null;

    const [, distanceTrunkCuspMax] = trunkCusp.getRange();
    const [, distanceTrunkMax] = trunk.getRange();
    const [distanceLeftMin, distanceLeftMax] = left.getRange();
    const [distanceRightMin, distanceRightMax] = right.getRange();

    if (masterIndex <= distanceTrunkCuspMax) {
        return { branch: CenterlineDistanceBranchSelection.CUSP, index: masterIndex };
    }
    if (masterIndex >= distanceLeftMin && masterIndex <= distanceLeftMax) {
        return {
            branch: CenterlineDistanceBranchSelection.LEFT,
            index: (masterIndex - distanceLeftMin) + distanceTrunkMax + 1,
        };
    }
    if (masterIndex >= distanceRightMin && masterIndex <= distanceRightMax) {
        return {
            branch: CenterlineDistanceBranchSelection.RIGHT,
            index: (masterIndex - distanceRightMin) + distanceTrunkMax + 1,
        };
    }
    return null;
}